/**
 * Niyo Jewellé — shared pricing API client.
 *
 * Exposes window.NiyoRates for the silver rate bar, the pricing page and the
 * resale form so they all show the same numbers from one request.
 *
 * Config: window.NiyoRatesConfig = { apiBase: 'https://api.niyojewelle.com' }
 * (rendered by snippets/niyo-rates-loader.liquid from theme settings).
 */
(function () {
  'use strict';

  if (window.NiyoRates) return;

  var CACHE_KEY = 'niyo_rates_v4';
  var CACHE_TTL = 15 * 60 * 1000;
  var REQUEST_TIMEOUT = 10000;
  var LIVE_KEYS = ['ratePerGram', 'rate_per_gram', 'liveRate', 'live_rate', 'silverRate', 'silver_rate'];
  var RESALE_KEYS = [
    'resaleRatePerGram',
    'resale_rate_per_gram',
    'oldSilverRate',
    'old_silver_rate',
    'resaleRate',
    'resale_rate',
  ];

  var inflight = null;

  function apiBase() {
    var config = window.NiyoRatesConfig || {};
    return String(config.apiBase || 'https://api.niyojewelle.com').replace(/\/+$/, '');
  }

  function toRate(value) {
    var n = parseFloat(value);
    return isFinite(n) && n > 0 ? n : null;
  }

  function pick(source, keys) {
    if (!source || typeof source !== 'object') return null;
    for (var i = 0; i < keys.length; i++) {
      var n = toRate(source[keys[i]]);
      if (n) return n;
    }
    return null;
  }

  function unwrap(json) {
    var data = (json && json.data) || json || {};
    return { data: data, silver: data.silver || data };
  }

  function request(path, options) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, REQUEST_TIMEOUT) : null;
    var init = Object.assign({ headers: { Accept: 'application/json' } }, options || {});
    if (controller) init.signal = controller.signal;

    return fetch(apiBase() + path, init)
      .then(function (res) {
        return res
          .json()
          .catch(function () { return {}; })
          .then(function (json) {
            if (!res.ok || json.success === false) {
              var error = new Error((json && (json.message || json.error)) || 'Request failed (' + res.status + ')');
              error.status = res.status;
              // Field-level messages from the API's validation (400), keyed by field name.
              error.details = (json && json.details) || null;
              throw error;
            }
            return json;
          });
      })
      .finally(function () {
        if (timer) clearTimeout(timer);
      });
  }

  /** Resale programme rules managed in the admin panel (Resale Rules). */
  function parseRules(data) {
    if (!data || typeof data !== 'object' || data.resalePercent === undefined) return null;
    var num = function (v) { var n = parseFloat(v); return isFinite(n) ? n : null; };
    return {
      resalePercent: num(data.resalePercent),
      pickupFee: num(data.pickupFee),
      minWeightGrams: num(data.minWeightGrams),
      maxWeightGrams: num(data.maxWeightGrams),
      acceptingRequests: data.acceptingRequests !== false,
    };
  }

  /** Sale offer from the pricing settings: { percent, label } while active, else null. */
  function parseOffer(data) {
    if (!data || typeof data !== 'object') return null;
    var percent = parseFloat(data.makingDiscountPercent);
    if (!data.active || !isFinite(percent) || percent <= 0) return null;
    var label = typeof data.label === 'string' ? data.label.trim().slice(0, 40) : '';
    return { percent: Math.round(percent * 100) / 100, label: label || null };
  }

  /**
   * Marks the page with the running offer so every "30% OFF" label shows it
   * (snippets/niyo-offer-badge.liquid), including cards added later.
   */
  function applyOffer(offer) {
    var root = document.documentElement;
    if (offer && offer.percent > 0) {
      root.setAttribute('data-niyo-offer', String(offer.percent));
      // JSON.stringify gives a quoted string, valid as a CSS `content` value.
      root.style.setProperty('--niyo-offer-badge-text', JSON.stringify(offer.percent + '% OFF'));
    } else {
      root.removeAttribute('data-niyo-offer');
      root.style.removeProperty('--niyo-offer-badge-text');
    }
  }

  function readCache() {
    try {
      var cached = JSON.parse(localStorage.getItem(CACHE_KEY));
      if (cached && Date.now() - cached.t < CACHE_TTL && (cached.live || cached.resale)) {
        return { live: cached.live || null, resale: cached.resale || null, rules: cached.rules || null, offer: cached.offer || null, cached: true };
      }
    } catch (e) {
      /* storage unavailable */
    }
    return null;
  }

  function writeCache(rates) {
    try {
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ t: Date.now(), live: rates.live, resale: rates.resale, rules: rates.rules, offer: rates.offer })
      );
    } catch (e) {
      /* storage unavailable */
    }
  }

  function fetchRates() {
    var current = request('/api/v1/rates/current')
      .then(function (json) {
        var out = unwrap(json);
        out.offer = json && json.offer; // sits next to `data` in the response
        return out;
      })
      .catch(function () { return null; });
    var resale = request('/api/v1/resale/rate').then(unwrap).catch(function () { return null; });

    return Promise.all([current, resale]).then(function (results) {
      var cur = results[0];
      var res = results[1];
      var rates = {
        live: cur ? pick(cur.silver, LIVE_KEYS) || pick(cur.data, LIVE_KEYS) : null,
        resale:
          (res && (pick(res.silver, RESALE_KEYS) || pick(res.data, RESALE_KEYS))) ||
          (cur && (pick(cur.silver, RESALE_KEYS) || pick(cur.data, RESALE_KEYS))) ||
          null,
        rules: res ? parseRules(res.data) : null,
        offer: cur ? parseOffer(cur.offer) : null,
      };
      if (!rates.live && !rates.resale) throw new Error('Silver rates are unavailable');
      writeCache(rates);
      return rates;
    });
  }

  /**
   * Resolve the current rates as { live, resale } numbers (₹ per gram) plus
   * `rules` (resale %, pickup fee, weight limits, acceptingRequests) when the
   * resale endpoint answered. Missing values are null — callers keep their
   * server-rendered fallback.
   * @param {{ force?: boolean }} [options]
   * @returns {Promise<{ live: number|null, resale: number|null, offer: { percent: number, label: string|null }|null }>}
   */
  function get(options) {
    if (!(options && options.force)) {
      var cached = readCache();
      if (cached) {
        applyOffer(cached.offer);
        return Promise.resolve(cached);
      }
    }
    if (!inflight) {
      inflight = fetchRates()
        .then(function (rates) {
          applyOffer(rates.offer);
          return rates;
        })
        .finally(function () {
          inflight = null;
        });
    }
    return inflight;
  }

  /**
   * @param {number} value
   * @returns {string} e.g. "₹238.00 / g"
   */
  function format(value) {
    return '₹' + Number(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' / g';
  }

  /**
   * @param {number} value
   * @returns {string} e.g. "₹1,234.50"
   */
  function formatMoney(value) {
    return '₹' + Number(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /**
   * Submit a resale / trade-in request. Rejects on network or API errors so
   * the caller never shows a success state that the backend did not confirm.
   * @param {object} payload
   * @returns {Promise<object>}
   */
  function submitResale(payload) {
    return request('/api/v1/resale/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
  }

  /**
   * Silver Circle: HUID(s) + order number → piece details and eligibility
   * (contact details only when the HUID and order number match).
   * @param {{ huids: string, orderNumber?: string }} payload
   */
  function lookupResale(payload) {
    return request('/api/v1/resale/lookup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
  }

  window.NiyoRates = {
    get: get,
    format: format,
    formatMoney: formatMoney,
    submitResale: submitResale,
    lookupResale: lookupResale,
  };

  document.dispatchEvent(new CustomEvent('niyo:rates-ready'));
})();
